import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	useGetTargetBreakdownQuery,
	useUpdateTargetBreakdownMutation,
	useAddTargetBreakdownMutation
} from '@features/crm/v3/target/api/targetApi';

import {
	getMarkets as fetchAllMarkets,
	getCategories as fetchAllCategories,
	getBrands as fetchAllBrands,
	getFabricTypes as fetchAllFabricTypes
} from '@store/design/action';

import ModalContainer from '@/components/modals/ModalContainer';
import ModalHeader from '@/components/modals/ModalHeader';
import { getAllBrands, getAllFabricTypes, getAllMarkets, getAllCategories } from '@/store/reducers';
import CustomDropdown from '@/components/CustomDropdown';
import SetTargetModal from '@/features/crm/v3/target/components/SetTargetModal';
import { toastWarning, toastSuccess } from '@/components/Toast';
import { checkUserType, getUserInfo, printApiError } from '@/utils/Util';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import LoaderComponent from '@/components/LoaderComponent';
import { formatToMK, parseSalesValue, validateSalesFormat } from '@/features/crm/utils/helpers';

const TargetDetailsModal = ({ onCloseModal }) => {
	const filterData = useSelector((store) => store.filterFieldStore);
	const targetItem = useSelector((state) => state?.crmCommonStore?.selectedCRMTarget);
	const [openSetTargetModal, setOpenSetTargetModal] = useState(false);
	const [tableData, setTableData] = useState({
		total: {
			design: targetItem?.targetNoOfNitexDesign || 0,
			sales: formatToMK(targetItem?.targetAmount || 0)
		},
		uncategorized: {
			design: targetItem?.targetNoOfNitexDesign || 0,
			sales: formatToMK(targetItem?.targetAmount || 0)
		},
		rows: []
	});
	const [editingRow, setEditingRow] = useState(null);
	const [newRow, setNewRow] = useState(null);
	const [totalAssignedDesign, setTotalAssignedDesign] = useState(0);
	const [totalAssignedSales, setTotalAssignedSales] = useState(0);
	const [remainingValues, setRemainingValues] = useState({
		design: targetItem?.teamTargetNoOfNitexDesign || 0,
		sales: formatToMK(targetItem?.teamTargetAmount || 0)
	});

	const dispatch = useDispatch();

	const [updateTargetBreakdown] = useUpdateTargetBreakdownMutation();
	const [addTargetBreakdown] = useAddTargetBreakdownMutation();

	let allBrands = useSelector(getAllBrands)?.map((item) => ({
		id: item.id,
		value: item.name
	}));
	let allMarkets = useSelector(getAllMarkets)?.map((item) => ({
		id: item.id,
		value: item.name
	}));
	let allCategories = useSelector(getAllCategories)?.map((item) => ({
		id: item.id,
		value: item.name
	}));
	let allFabricTypes = useSelector(getAllFabricTypes)?.map((item) => ({
		id: item.code,
		value: item.value
	}));

	const { data: targetBreakdownData, isLoading } = useGetTargetBreakdownQuery(targetItem?.salesTargetId, {
		skip: !targetItem?.salesTargetId
	});

	useEffect(() => {
		dispatch(fetchAllBrands());
		dispatch(fetchAllMarkets());
		dispatch(fetchAllCategories());
		dispatch(fetchAllFabricTypes());
	}, []);

	useEffect(() => {
		if (targetBreakdownData) {
			const formattedRows = targetBreakdownData.map((row) => ({
				id: row?.id,
				salesTargetId: row?.salesTargetId,
				brand: { id: row?.brandResponse?.id, value: row?.brandResponse?.name },
				market: {
					id: row?.productGroupResponse?.id,
					value: row?.productGroupResponse?.name
				},
				category: {
					id: row?.categoryResponse?.id,
					value: row?.categoryResponse?.name
				},
				fabric: {
					id: row?.fabricTypeResponse?.code,
					value: row?.fabricTypeResponse?.value
				},
				design: row?.targetDesignNumber,
				sales: formatToMK(row?.targetGMV),
				brandResponse: row?.brandResponse,
				productGroupResponse: row?.productGroupResponse,
				categoryResponse: row?.categoryResponse,
				fabricTypeResponse: row?.fabricTypeResponse,
				targetGMV: row?.targetGMV,
				targetDesignNumber: row?.targetDesignNumber
			}));
			setTableData((prevData) => ({
				...prevData,
				rows: formattedRows,
				uncategorized: calculateRemainingValues(formattedRows)
			}));
		}
	}, [targetBreakdownData]);

	const calculateRemainingValues = (rows) => {
		const totalDesign = rows.reduce((sum, row) => sum + (row.design || 0), 0) + (newRow?.design || 0);
		setTotalAssignedDesign(totalDesign);
		const totalSales =
			rows.reduce((sum, row) => sum + parseSalesValue(row.sales), 0) +
			parseSalesValue(newRow?.sales || '0');
		setTotalAssignedSales(totalSales);

		const remainingDesign = targetItem?.targetNoOfNitexDesign - totalDesign;
		const remainingSales = targetItem?.targetAmount - totalSales;

		return {
			design: remainingDesign,
			sales: formatToMK(remainingSales)
		};
	};

	useEffect(() => {
		setRemainingValues(calculateRemainingValues(tableData.rows));
	}, [tableData, newRow, targetItem]);

	const formatSalesValue = (value) => {
		if (typeof value === 'string') {
			return value.startsWith('$') ? value : `$${value}`;
		}
		return `$${formatToMK(value)}`;
	};

	const validateRow = (row) => {
		if (!row.brand) {
			toastWarning('Brand is required');
			return false;
		}

		if (row.design === '') {
			toastWarning('Design target is required');
			return false;
		}

		if (!validateSalesFormat(`$${row.sales}`)) {
			toastWarning('Sales target must be in K or M format (e.g., $1K or $1M)');
			return false;
		}

		if (totalAssignedDesign > targetItem?.targetNoOfNitexDesign) {
			toastWarning('Cannot exceed total available design values');
			return false;
		}
		if (totalAssignedSales > targetItem?.targetAmount) {
			toastWarning('Cannot exceed total available sales values');
			return false;
		}

		return true;
	};

	const handleEdit = (rowId) => {
		setEditingRow(rowId);
	};

	const handleSaveTargetBreakdown = async (row, isNew = false) => {
		if (!validateRow(row)) return;

		try {
			dispatch(openLoader());
			const requestBody = {
				salesTargetId: isNew ? targetItem?.salesTargetId : row?.salesTargetId,
				brandId: row?.brand?.id,
				productGroupId: row?.market?.id,
				categoryId: row?.category?.id,
				fabricType: row?.fabric?.id,
				targetNoOfNitexDesign: row?.design,
				targetAmount: parseSalesValue(row?.sales)
			};

			if (isNew) {
				await addTargetBreakdown(requestBody).unwrap();
				toastSuccess('New target breakdown added successfully');
			} else {
				await updateTargetBreakdown({
					requestBody: requestBody,
					params: row.id
				}).unwrap();
				toastSuccess('Target breakdown updated successfully');
			}

			setNewRow(null);
			setEditingRow(null);
			dispatch(closeLoader());
		} catch (error) {
			printApiError(error);
			dispatch(closeLoader());
		}
	};

	const handleSave = async (rowId) => {
		const row = tableData.rows.find((r) => r.id === rowId);
		await handleSaveTargetBreakdown(row);
	};

	const handleAddBreakdown = () => {
		const newRow = {
			id: null,
			brand: null,
			market: null,
			category: null,
			fabric: null,
			design: '',
			sales: ''
		};
		setNewRow(newRow);
		setEditingRow(newRow.id);
	};

	const handleInputChange = (rowId, field, value) => {
		if (field === 'sales') {
			const cleanValue = value?.toString().replace(/[^0-9.KMkm]/g, '');
			const parts = cleanValue?.split('.');
			if (parts?.length > 2) return;

			if (
				cleanValue?.endsWith('K') ||
				cleanValue?.endsWith('M') ||
				cleanValue?.endsWith('k') ||
				cleanValue?.endsWith('m')
			) {
				if (!validateSalesFormat(`$${cleanValue}`)) {
					toastWarning('Invalid format. Use K for thousands or M for millions (e.g., $1K or $1M)');
					return;
				}
			}

			value = cleanValue;
		}

		if (rowId === newRow?.id) {
			setNewRow({ ...newRow, [field]: value });
		} else {
			setTableData({
				...tableData,
				rows: tableData.rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
			});
		}
	};

	const handleAddRow = async () => {
		await handleSaveTargetBreakdown(newRow, true);
	};

	const isEditDisabled = !targetItem?.targetEditableToList?.includes(getUserInfo().id);

	const renderRow = (row, isNew = false) => {
		const isEditing = editingRow === row.id;

		return (
			<tr key={row.id} className={isEditing ? 'editing' : ''}>
				<td>
					{isEditing ? (
						<div className="transparent-select">
							<CustomDropdown
								title="Brand"
								items={allBrands}
								selectedItem={row.brand?.value || 'Select Brand'}
								toolTipLength={12}
								onItemClick={(item) => handleInputChange(row.id, 'brand', item)}
							/>
						</div>
					) : (
						row.brandResponse?.name
					)}
				</td>
				<td>
					{isEditing ? (
						<div className="transparent-select">
							<CustomDropdown
								title="Market"
								items={allMarkets}
								selectedItem={row.market?.value || 'Select Market'}
								toolTipLength={12}
								onItemClick={(item) => handleInputChange(row.id, 'market', item)}
							/>
						</div>
					) : (
						row.productGroupResponse?.name
					)}
				</td>
				<td>
					{isEditing ? (
						<div className="transparent-select">
							<CustomDropdown
								title="Category"
								items={allCategories}
								selectedItem={row.category?.value || 'Select Category'}
								toolTipLength={12}
								onItemClick={(item) => handleInputChange(row.id, 'category', item)}
							/>
						</div>
					) : (
						row.categoryResponse?.name
					)}
				</td>
				<td>
					{isEditing ? (
						<div className="transparent-select">
							<CustomDropdown
								title="Fabric Type"
								items={allFabricTypes}
								selectedItem={row.fabric?.value || 'Select Fabric'}
								toolTipLength={12}
								onItemClick={(item) => handleInputChange(row.id, 'fabric', item)}
							/>
						</div>
					) : (
						row.fabricTypeResponse?.value
					)}
				</td>
				<td>
					{isEditing ? (
						<input
							type="number"
							value={row.design}
							onChange={(e) => handleInputChange(row.id, 'design', parseInt(e.target.value))}
							className="no-border"
							placeholder="0"
						/>
					) : (
						row.targetDesignNumber
					)}
				</td>
				<td>
					{isEditing ? (
						<input
							type="text"
							value={row.sales}
							onChange={(e) => handleInputChange(row.id, 'sales', e.target.value)}
							placeholder="$1K or $1M"
							className="no-border"
						/>
					) : (
						formatSalesValue(row.targetGMV)
					)}
				</td>
				<td>
					{isEditing ? (
						<button
							className="button text-only blue"
							onClick={() => (isNew ? handleAddRow() : handleSave(row.id))}
						>
							Save
						</button>
					) : (
						<button
							className="button text-only blue"
							onClick={() => handleEdit(row.id)}
							disabled={(!checkUserType('ADMIN') && isEditDisabled) || targetItem?.isFinalized}
						>
							Edit
						</button>
					)}
					{isEditing && (
						<button
							className="button text-only ml-12 red"
							onClick={() => {
								if (isNew) {
									setNewRow(null);
								}
								setEditingRow(null);
							}}
						>
							Cancel
						</button>
					)}
				</td>
			</tr>
		);
	};

	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer
				className={`target-details-modal ${openSetTargetModal ? 'set-target-opened' : ''}`}
				onCloseModal={() => {}}
			>
				<div className="common-popup">
					<ModalHeader
						title={`Breakdown of ${targetItem?.userName}'s Target in ${filterData?.quarter[0]?.key}`}
						onClose={onCloseModal}
					/>
					<div className="common-popup-body">
						{targetItem?.salesTargetId ? (
							<table className="table height-sm with-border">
								<thead>
									<tr>
										<th>Brand</th>
										<th>Market</th>
										<th>Category</th>
										<th>Fabric</th>
										<th>Design Target</th>
										<th>Sales Target</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{tableData.rows.map((row) => renderRow(row))}
									{newRow && renderRow(newRow, true)}
									<tr>
										<td colSpan={4}>
											<span className="semibold-14">Uncategorized</span>
										</td>
										<td>
											<span className="semibold-14">{remainingValues.design}</span>
										</td>
										<td>
											<span className="semibold-14">
												{formatSalesValue(remainingValues.sales)}
											</span>
										</td>
										<td>
											<button
												className="button text-only blue"
												onClick={handleAddBreakdown}
												disabled={
													(totalAssignedDesign ===
														targetItem?.targetNoOfNitexDesign &&
														totalAssignedSales === targetItem?.targetAmount) ||
													(!checkUserType('ADMIN') && isEditDisabled)
												}
											>
												Add breakdown
											</button>
										</td>
									</tr>
									<tr>
										<td colSpan={4}>
											<span className="semibold-14">Total</span>
										</td>
										<td>
											<span className="semibold-14">
												{targetItem?.targetNoOfNitexDesign}
											</span>
										</td>
										<td>
											<span className="semibold-14">
												{formatSalesValue(targetItem?.targetAmount)}
											</span>
										</td>
										<td>
											<button
												className="button text-only blue"
												onClick={() => setOpenSetTargetModal(true)}
												disabled={
													(!checkUserType('ADMIN') && isEditDisabled) ||
													targetItem?.isFinalized
												}
											>
												Edit
											</button>
										</td>
									</tr>
								</tbody>
							</table>
						) : (
							<div className="alert alert-secondary text-center w-100 gap-sm">
								<p>Target not set yet</p>
								<button
									className="button text-only blue mt-12"
									onClick={() => setOpenSetTargetModal(true)}
									disabled={
										(!checkUserType('ADMIN') && isEditDisabled) || targetItem?.isFinalized
									}
								>
									Set Target
								</button>
							</div>
						)}
					</div>
				</div>
			</ModalContainer>
			{openSetTargetModal && (
				<SetTargetModal
					targetId={targetItem?.salesTargetId || null}
					onCloseModal={() => setOpenSetTargetModal(false)}
				/>
			)}
		</LoaderComponent>
	);
};

export default TargetDetailsModal;
